import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { MusicPlayer } from './MusicPlayer';
import { MobileBottomNav } from './MobileBottomNav';
import { MobileTopBar } from './MobileTopBar';
import { Helmet } from 'react-helmet-async';
import { usePageTransition } from '../hooks/usePageTransition';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from './ErrorFallback';

const pageTitles: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Bible Me - Discover Biblical Wisdom & Test Your Knowledge',
    description: 'Explore Bible verses, test your biblical knowledge with trivia, and listen to inspiring Christian music. Your daily companion for spiritual growth.'
  },
  '/verses': {
    title: 'Verse Finder - Bible Me',
    description: 'Search and discover Bible verses that speak to your heart. Find comfort, wisdom, and guidance in God\'s Word.'
  },
  '/trivia': {
    title: 'Bible Trivia - Test Your Knowledge',
    description: 'Challenge yourself with our Bible trivia game. Learn more about Scripture while having fun!'
  },
  '/study': {
    title: 'Bible Study - Deepen Your Understanding',
    description: 'Engage in structured Bible study plans and grow in your faith journey.'
  },
  '/profile': {
    title: 'Your Profile - Bible Me',
    description: 'View your saved verses, trivia scores, and manage your Bible Me profile.'
  },
  '/contact': {
    title: 'Contact Us - Bible Me',
    description: 'Get in touch with us. Share your feedback, questions, or suggestions about Bible Me.'
  }
};

export default function Layout() {
  const location = useLocation();
  const pageInfo = pageTitles[location.pathname] || pageTitles['/'];
  usePageTransition();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-blue-950/20 flex flex-col transition-all duration-500">
      <Helmet>
        <title>{pageInfo.title}</title>
        <meta name="description" content={pageInfo.description} />
      </Helmet>

      {/* Subtle Background Pattern */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.02] dark:opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Desktop Navbar */}
      <div className="hidden md:block">
        <Navbar />
      </div>

      {/* Mobile Top Bar */}
      <MobileTopBar />

      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <main className="relative flex-grow container mx-auto px-4 py-2 mt-[60px] md:mt-20 mb-20 md:mb-0 page-transition">
          <Outlet />
        </main>
      </ErrorBoundary>

      {/* Desktop spacing and footer */}
      <div className="hidden md:block h-48" />

      {/* Music Player - Hidden on mobile, shown on desktop */}
      <div className="hidden md:block fixed bottom-20 left-0 right-0 z-40">
        <div className="container mx-auto px-4">
          <MusicPlayer />
        </div>
      </div>

      {/* Desktop Footer */}
      <div className="hidden md:block fixed bottom-0 left-0 right-0 z-30">
        <Footer />
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}